import { BufferAttribute, PlaneGeometry, Triangle, Vector2 } from 'three';
import { Matrix4 } from 'three';
import { Group, Line, Mesh, Shape,
	Vector3, Quaternion,
	BufferGeometry, ShapeGeometry,
	MeshBasicMaterial, LineBasicMaterial, DoubleSide, Euler, Plane } from 'three';
import { CustomBox } from './customBox';
import { Loader3d } from './loader3d';


export class Dimension {
	
	static timer = {
		_addLines: 0,
		_addArrows: 0,
		_addText: 0
	};

	static font = null;

	constructor( ) {
		this._delta = 0.01;//in radians;
		this._matCache = {};

		this.temp = {
			euler: new Euler(),
			quaternion: new Quaternion(),
			matrix: new Matrix4(),
			matrix2: new Matrix4(),
			xAxis: new Vector3( 1, 0, 0 ),
			yAxis: new Vector3( 0, 1, 0 ),
			zAxis: new Vector3( 0, 0, 1 )
		};

		this._textCache = new Map();
		this._clickPlaneMaterial = new MeshBasicMaterial( { color: 0x000000, opacity: 0.005, transparent: true } );
	}

	/**
	 * @param {Vector3} measureStartPoint - The start point of the measured object
	 * @param {Vector3} measureEndPoint - The end point of the measured object
	 * @param {Vector3} startPoint - The start point of the dimension
	 * @param {string} text - The text of the dimension
	 * @param {number} textSize - The size of the text
	 * @param {number} color - The color of the dimension. Default value 0xff0000
	 * @param {number} textAngle - Rotation of text in radians. Default value 0
	 * @param {string} reverse - If text should be reversed. Default value false
	 * @param {boolean} changeable - If text should be changeable. Default value false
	 */
	async createLinear( measureStartPoint, measureEndPoint, startPoint, text, textSize, color = 0xff0000, textAngle, reverse = false, changeable = false ) {
		this.measureStartPoint = measureStartPoint;
		this.measureEndPoint = measureEndPoint;
		this.startPoint = startPoint;
		this.text = text;
		this.textSize = textSize;
		this.textAngle = textAngle ? textAngle.clone() : new Vector3();

		//			startPoint 
		//			    <|-------------------TEXT-----------------|>|
		//				|											|
		//				|											|
		//				|											|
		//		measureStartPoint							  measureEndPoint


		//GENERAL PARAMETERS
		this.vectorfromStartToEnd = measureEndPoint.clone().sub( measureStartPoint );
		this.vectorfromStartToText = startPoint.clone().sub( measureStartPoint );
		this.endPoint = this._getEndPoint();
		let v = this.endPoint.clone().sub( this.startPoint ).normalize();
		this.rotationQuaternion = this.temp.quaternion.setFromUnitVectors( this.temp.xAxis, v );
		this.rotationEuler = this.temp.euler.setFromQuaternion( this.rotationQuaternion, 'XYZ' );
		this.textPoint = this.startPoint.clone().add( this.endPoint ).multiplyScalar( 0.5 );
		//main group
		let dimension = new Group();
		dimension.name = 'Dimension';

		//create material
		let { lineMat, meshMat } = this._getMats( color );

		this._addLines( dimension, lineMat );

		this._addArrows( dimension, meshMat );

		await this._addText( dimension, meshMat, changeable, reverse );

		//add dimension property to know it is a dimension.
		dimension.dimensionData = {
			measureStartPoint: measureStartPoint,
			measureEndPoint: measureEndPoint,
			startPoint: startPoint,
			text: text,
			textSize: textSize,
			color: color,
			textAngle: textAngle,
			changeable: changeable
		};

		return dimension;
	}

	_addLines( dimension, material ) {

		//create line geometry from measureStartPoint to measureEndPoint
		let linegeom = new BufferGeometry().setFromPoints( [ this.measureStartPoint, this.startPoint ] );
		//create another parallel to previous one but from measureEndPoint
		let linegeom2 = new BufferGeometry().setFromPoints( [ this.measureEndPoint, this.endPoint ] );

		//lateral lines
		dimension.add( new Line( linegeom, material ) );
		dimension.add( new Line( linegeom2, material ) );

		//line from start to end
		let linegeom3 = new BufferGeometry().setFromPoints( [ this.startPoint, this.endPoint ] );

		dimension.add( new Line( linegeom3, material ) );
	}

	_addArrows( dimension, material ) {

		this._createArrowGeometry();

		dimension.add( new Mesh( this._startArrowGeometry, material ) );
		dimension.add( new Mesh( this._endArrowGeometry, material ) );
	}

	_createArrowGeometry() {

		//get arrow direction vector
		//   measureStart ------------------------ measureEnd
		//		|\										|
		//		| \	arrow direction vector( v1 )		|
		//		|  \									|	
		//		|										|
		//	  start ---------------------------------- end

		const rotation_of_30_degrees = Math.PI / 6;

		const v1 = this._getVectorAtAngle( this.vectorfromStartToEnd, this.vectorfromStartToText, rotation_of_30_degrees );
		const v2 = this._getVectorAtAngle( this.vectorfromStartToEnd, this.vectorfromStartToText, -rotation_of_30_degrees );

		//right & left arrows
		const doGeom = ( v1,v2,v3 ) => {
			const geom = new BufferGeometry();

			// Define vertices
			const vertices = new Float32Array( [ 
				v1.x, v1.y, v1.z,
				v2.x, v2.y, v2.z,
				v3.x, v3.y, v3.z
			] );

			// Define the index for the face
			const indices = new Uint32Array( [ 0, 1, 2 ] );

			// Add vertices and indices to the geometry
			geom.setAttribute('position', new BufferAttribute(vertices, 3));
			geom.setIndex(new BufferAttribute(indices, 1));

			// Compute face normals for lighting calculations
			geom.computeVertexNormals();

			return geom;
		};

		const scale = 0.55;
		this._startArrowGeometry = doGeom( 
			this.startPoint.clone(),
			this.startPoint.clone().add( v1.clone().multiplyScalar( scale ) ), 
			this.startPoint.clone().add( v2.clone().multiplyScalar( scale ) ) );
		this._endArrowGeometry = doGeom( 
			this.endPoint.clone(),
			this.endPoint.clone().add( v1.clone().negate().multiplyScalar( scale ) ), 
			this.endPoint.clone().add( v2.clone().negate().multiplyScalar( scale ) ) );
	}

	_getVectorAtAngle(v1, v2, angle) {
		// Step 1: Normalize the input vectors
		v1 = v1.clone().normalize();
		v2 = v2.clone().normalize();
		
		// Step 2: Rotate the halfway vector by the specified angle
		// The rotation axis is perpendicular to the plane formed by v1 and v2
		let rotationAxis = new Vector3().crossVectors(v1, v2).normalize();
	
		// Create a quaternion for the rotation
		let quaternion = new Quaternion();
		quaternion.setFromAxisAngle(rotationAxis, angle);
	
		// Apply the rotation to the halfway vector
		let result = v1.applyQuaternion(quaternion);
	
		return result.normalize();
	}

	async _addText( dimension, material, changeable, reverse = false ) {

		await this._loadFont();
		if( !Dimension.font ) return dimension;

		//set dimension text axis
		this._setAxis();

		//set orientation. text always looking up direction
		if( this.rotationEuler.z < 0 ) {
			this.rotationEuler.z += Math.PI;
			this.rotationQuaternion.setFromEuler( this.rotationEuler );
		}

		const str = typeof this.text === 'string' ? this.text : this.text.toString();

		const textg = this._textCache.has( str ) ? this._textCache.get( str ).clone() : new ShapeGeometry( Dimension.font.generateShapes( str, this.textSize ) );
		if( !this._textCache.has( str ) ) this._textCache.set( str, textg );

		//align to center
		textg.computeBoundingBox();
		let textHeight = ( textg.boundingBox.max.y - textg.boundingBox.min.y );
		let textWidth = ( textg.boundingBox.max.x - textg.boundingBox.min.x );
		textg.translate( -0.5 * textWidth, 0, 0 );

		this._rotate( textg, reverse );
		
		//rotate by user angle
		//get normal vector from three vectors
		if( this.textAngle.x !== 0 || this.textAngle.y !== 0 || this.textAngle.z !== 0 ) {

			//x vector
			let qX = this.temp.quaternion.setFromAxisAngle( this.temp.xAxis, this.textAngle.x );
			textg.applyQuaternion( qX );
			//y vector
			let qY = this.temp.quaternion.setFromAxisAngle( this.temp.yAxis, this.textAngle.y );
			textg.applyQuaternion( qY );
			//z vector
			let qZ = this.temp.quaternion.setFromAxisAngle( this.temp.zAxis, this.textAngle.z );
			textg.applyQuaternion( qZ );
		}
		
		//move to position
		textg.translate( this.textPoint.x, this.textPoint.y, this.textPoint.z );

		//try to move outside the line
		let textOrientation = this.startPoint.clone().sub( this.measureStartPoint ).normalize().multiplyScalar( textHeight * 1.1 );
		textg.translate( textOrientation.x, textOrientation.y, textOrientation.z );

		//create mesh
		let mesh = new Mesh( textg, material );
		mesh.name = 'textMesh';

		//create text group
		const text = new Group();
		text.name = 'text';

		if( changeable ) {
			//create plane of the same size of the mesh so raycast can be done againts it for when a user want to change the value double clicking
			const plane = this._createClickingPlane( mesh );
			plane.name = 'clickingPlane';
			text.add( plane );
		}

		text.add( mesh );

		dimension.add( text );
	}

	_setAxis(){
		let v1 = this.measureStartPoint.clone().sub( this.startPoint );
		let v2 = this.measureStartPoint.clone().sub( this.measureEndPoint );

		//set text axis
		this.__xTextAxis = this.vectorfromStartToEnd.clone().normalize();
		this.__yTextAxis = this.startPoint.clone().sub( this.measureStartPoint ).normalize();
		this.__zTextAxis = v2.clone().cross( v1 ).normalize();
	}

	_rotate( textg, reverse ) {

		//get dim axis and dimToText axis
		const getAxis = v => {
			const distance = [
				v.distanceTo( new Vector3( 1, 0, 0 ) ),
				v.distanceTo( new Vector3( -1, 0, 0 ) ),
				v.distanceTo( new Vector3( 0, 1, 0 ) ),
				v.distanceTo( new Vector3( 0, -1, 0 ) ),
				v.distanceTo( new Vector3( 0, 0, 1 ) ),
				v.distanceTo( new Vector3( 0, 0, -1 ) )
			];
			let minIndex = distance.indexOf( Math.min.apply( Math,distance ) );
			if( minIndex === 0 ) return 'x';
			else if( minIndex === 1 ) return '-x';
			else if( minIndex === 2 ) return 'y';
			else if( minIndex === 3 ) return '-y';
			else if( minIndex === 4 ) return 'z';
			else return'-z';
		};
		let dimAxis = getAxis( this.vectorfromStartToEnd.clone().normalize() );
		let textAxis = getAxis( this.vectorfromStartToText.clone().normalize() );

		let rotateX = 0, rotateY = 0, rotateZ = 0;
		switch ( dimAxis ) {
			case 'x': {
				switch ( textAxis ) {
					case 'y':
					case '-y': {} break;
					case 'z':
					case '-z':{ rotateX = -Math.PI / 2; } break;
				}
			} break;
			case '-x': {
				switch ( textAxis ) {
					case 'y':
					case '-y': {} break;
					case 'z':
					case '-z':{ rotateX = -Math.PI / 2; } break;
				}
			} break;
			case 'y': {
				switch ( textAxis ) {
					case 'x':
					case '-x': { rotateZ = Math.PI / 2; } break;
					case 'z':
					case '-z':{ rotateZ = Math.PI / 2; rotateX = -Math.PI / 2; } break;
				}
			} break;
			case '-y': {
				switch ( textAxis ) {
					case 'x':
					case '-x': { rotateZ = Math.PI / 2; } break;
					case 'z':
					case '-z':{ rotateZ = Math.PI / 2; rotateX = -Math.PI / 2; } break;
				}
			} break;
			case 'z': {
				switch ( textAxis ) {
					case 'x':
					case '-x': { rotateX = -Math.PI / 2; rotateY = Math.PI / 2; } break;
					case 'y':
					case '-y':{ rotateY = -Math.PI / 2; } break;
				}
			} break;
			case '-z': {
				switch ( textAxis ) {
					case 'x':
					case '-x': { rotateX = -Math.PI / 2; rotateY = Math.PI / 2; } break;
					case 'y':
					case '-y':{ rotateY = Math.PI / 2; } break;
				}
			} break;
		}

		textg.rotateX( rotateX );
		textg.rotateY( rotateY );
		textg.rotateZ( rotateZ );

		if( reverse ) {
			if( textAxis === 'x' ) textg.rotateX( Math.PI );
			else if( textAxis === 'y' ) textg.rotateY( Math.PI );
			else if( textAxis === 'z' ) textg.rotateZ( Math.PI );
		}
	}

	//Method that returns the euler angles between two coordinate systems
	getEulerAngles( axis1, axis2, axis3, target1, target2, target3 ) {

		this.temp.matrix.makeBasis( axis1, axis2, axis3 );

		this.temp.matrix2.makeBasis( target1, target2, target3 );

		this.temp.matrix.multiplyMatrices( this.temp.matrix2, this.temp.matrix.invert() );

		this.temp.euler.setFromRotationMatrix( this.temp.matrix );

		return [ this.temp.euler.x, this.temp.euler.y, this.temp.euler.z ];
	}

	async _loadFont() {
		if( Dimension.font ) return;
		try{
			Dimension.font = await new Loader3d().loadFont();
		}
		catch( e ) {
			console.log( e );
			Dimension.font = null;
		}
	}

	_getPlaneRotation( normal1, normal2 ) {

		this.temp.quaternion.setFromUnitVectors( normal1 , normal2 );
		this.temp.euler.setFromQuaternion( this.temp.quaternion, 'XYZ' );

		//return quaternion;
		return this.temp.euler;
	}

	_getRotation() {
		//text will always be written in X,Y plane, so we get the angle between that plane and startPoint-measureStartPoint plane

		let pXY = new Plane().setFromCoplanarPoints( this.temp.yAxis, this.temp.xAxis, this.temp.zAxis ).normalize();
		let pText = new Plane().setFromCoplanarPoints( this.startPoint, this.measureStartPoint, this.measureEndPoint ).normalize();

		this.temp.quaternion.setFromUnitVectors( pText.normal , pXY.normal );
		this.temp.euler.setFromQuaternion( this.temp.quaternion, 'XYZ' );

		return this.temp.euler;
	}

	_getZAngle() {
		return this.temp.xAxis.angleTo( this.endPoint.clone().sub( this.startPoint ) );
	}

	_getEndPoint() {
		let triangle = new Triangle( this.measureStartPoint, this.measureEndPoint, this.startPoint );
		let normal = new Vector3(); triangle.getNormal( normal );

		let vectorFromStartMeasureToStart = this.startPoint.clone().sub( this.measureStartPoint ).normalize();
		let vectorFromStartToEnd = vectorFromStartMeasureToStart.clone().applyAxisAngle( normal, -Math.PI / 2 );
		let vectorFromStartToMeaureEnd = this.measureEndPoint.clone().sub( this.startPoint ).normalize();

		let angle = vectorFromStartToEnd.angleTo( vectorFromStartToMeaureEnd );
		let hypotenuse = this.measureEndPoint.distanceTo( this.startPoint );
		let opposite = Math.sin( angle ) * hypotenuse;

		let endPoint = this.measureEndPoint.clone().add( vectorFromStartMeasureToStart.multiplyScalar( opposite ) );

		return endPoint;
	}

	_getMats( color ) {
		let keyLine = 'dimensionLine' + color;
		let keyMesh = 'dimensionMesh' + color;

		if( !Object.prototype.hasOwnProperty.call( this._matCache, keyLine ) ) {
			this._matCache[ keyLine ] = new LineBasicMaterial( { name: keyLine } );
			this._matCache[ keyMesh ] = new MeshBasicMaterial( { name: keyMesh, side:DoubleSide } );

			this._matCache[ keyLine ].color.setHex( color );
			this._matCache[ keyMesh ].color.setHex( color );

			this._matCache[ keyLine ].depthTest = false;
			this._matCache[ keyMesh ].depthTest = false;
		}

		return { lineMat: this._matCache[ keyLine ], meshMat: this._matCache[ keyMesh ] };
	}

	dispose() {
		Object.keys( this._matCache ).forEach( key => this._matCache[ key ].dispose() );
		delete this._matCache;

		Dimension.font = null;
	}

	_createClickingPlane( mesh ) {
		let box = new CustomBox().setFromObject( mesh );

		const geometry = new PlaneGeometry( 1, 1 );
		if( Math.abs( box.max.x - box.min.x ) > Math.max( Math.abs( box.max.y - box.min.y ), Math.abs( box.max.z - box.min.z ) ) ) {
			geometry.attributes.position.setXYZ( 0, box.min.x, box.max.y, box.min.z );
			geometry.attributes.position.setXYZ( 1, box.max.x, box.max.y, box.min.z );
			geometry.attributes.position.setXYZ( 2, box.min.x, box.max.y, box.max.z );
			geometry.attributes.position.setXYZ( 3, box.max.x, box.max.y, box.max.z );
		} else if( Math.abs( box.max.y - box.min.y ) > Math.max( Math.abs( box.max.x - box.min.x ), Math.abs( box.max.z - box.min.z ) ) ) {
			geometry.attributes.position.setXYZ( 0, box.min.x, box.max.y, box.max.z );
			geometry.attributes.position.setXYZ( 1, box.max.x, box.max.y, box.max.z );
			geometry.attributes.position.setXYZ( 2, box.min.x, box.min.y, box.max.z );
			geometry.attributes.position.setXYZ( 3, box.max.x, box.min.y, box.max.z );
		} else if( Math.abs( box.max.z - box.min.z ) > Math.max( Math.abs( box.max.x - box.min.x ), Math.abs( box.max.y - box.min.y ) ) ) {
			geometry.attributes.position.setXYZ( 0, box.min.x, box.max.y, box.max.z );
			geometry.attributes.position.setXYZ( 1, box.min.x, box.max.y, box.min.z );
			geometry.attributes.position.setXYZ( 2, box.max.x, box.max.y, box.max.z );
			geometry.attributes.position.setXYZ( 3, box.max.x, box.max.y, box.min.z );
		}

		return new Mesh( geometry, this._clickPlaneMaterial );
	}
}